import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { objectStorageApi } from '../../../api/objectStorage';

export default function PopupDisplayManagement() {
  const endpoint = '/admin/ads/startup-popup/mini';
  const [value, setValue] = useState(null);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  const draft = useAdminDraftGuard(value, { enabled: value !== null, busy });
  const load = async () => {
    if (!draft.confirmDiscard()) return;
    try {
      const { data } = await api.getAuth(endpoint);
      const next = { fontSize: 14, ...data.data };
      setValue(next);
      draft.markClean(next);
      setError('');
    } catch {
      setError('설정을 불러오지 못했습니다.');
    }
  };
  useEffect(() => {
    void load();
  }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps
  const change = (key, next) => {
    setValue((v) => ({ ...v, [key]: next }));
    setSaved(false);
  };
  const save = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    setSaved(false);
    try {
      const { data } = await api.putAuth(endpoint, value);
      const next = { fontSize: 14, ...data.data };
      setValue(next);
      draft.markClean(next);
      setSaved(true);
    } catch (e) {
      setError(e.response?.data?.msg || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Box component="form" onSubmit={save} aria-label="미니 팝업 설정">
      <Stack spacing={2} component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0 }}>
        {error && (
          <Alert severity="error" action={<Button onClick={load}>재시도</Button>}>
            {error}
          </Alert>
        )}
        {!value ? (
          <Typography>설정을 불러오는 중…</Typography>
        ) : (
          <>
            <Typography variant="h6">미니 팝업 · 모든 탭</Typography>
            <Alert severity="info">
              하단 내비게이션이 있는 모든 탭에서 표시됩니다. 기본 높이 52pt이며 미니플레이어가
              있으면 그 위, 없으면 탭바 위에 12pt 간격으로 배치됩니다. 접속 광고·알림 안내·키보드와
              동시에 표시하지 않습니다. ×를 누르면 오늘은 숨깁니다.
            </Alert>
            <TextField
              required={value.enabled}
              label="문구"
              inputProps={{ maxLength: 100 }}
              value={value.title}
              onChange={(e) => change('title', e.target.value)}
              helperText="짧은 한두 문장을 권장합니다. 큰 글자에서는 높이가 늘어납니다."
            />
            <TextField
              label="영문 문구 (선택)"
              inputProps={{ maxLength: 100 }}
              value={value.titleEn}
              onChange={(e) => change('titleEn', e.target.value)}
            />
            <TextField
              required={value.enabled}
              label="랜딩 URL"
              type="url"
              value={value.destinationUrl}
              onChange={(e) => change('destinationUrl', e.target.value)}
            />
            <Button component="label" variant="outlined">
              선택 썸네일 업로드
              <input
                hidden
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (
                    !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) ||
                    file.size > 5 * 1024 * 1024
                  ) {
                    setError('PNG/JPG/WebP 5MB 이하를 선택하세요.');
                    return;
                  }
                  setBusy(true);
                  try {
                    const body = new FormData();
                    body.append('image', file);
                    const result = await objectStorageApi.uploadImage(body);
                    if (!result.data?.url) throw new Error();
                    change('imageUrl', result.data.url);
                  } catch {
                    setError('이미지를 업로드하지 못했습니다.');
                  } finally {
                    setBusy(false);
                  }
                }}
              />
            </Button>
            {value.imageUrl && <Button onClick={() => change('imageUrl', '')}>썸네일 제거</Button>}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="배너 배경 색상"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="color"
                value={value.backgroundColor}
                onChange={(e) => change('backgroundColor', e.target.value)}
              />
              <TextField
                label="문구 색상"
                fullWidth
                InputLabelProps={{ shrink: true }}
                type="color"
                value={value.textColor}
                onChange={(e) => change('textColor', e.target.value)}
              />
            </Stack>
            <Button
              variant="outlined"
              onClick={() => {
                change('backgroundColor', '#F16438');
                change('textColor', '#111111');
              }}
            >
              포스트팝 기본 색상으로
            </Button>
            <Typography variant="caption">
              기본: 포스트팝 주황 배경 · 검정 문구. 색상을 고른 뒤 아래 저장을 눌러 적용하세요.
            </Typography>
            <TextField
              select
              label="문구 크기"
              value={value.fontSize ?? 14}
              onChange={(e) => change('fontSize', Number(e.target.value))}
              helperText={`현재 ${value.fontSize ?? 14}pt · 기본 14pt. 줄높이도 크기에 맞춰 바뀝니다.`}
            >
              {[10, 11, 12, 13, 14].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}pt{size === 14 ? ' (기본)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="outlined" onClick={() => change('fontSize', 14)}>
              기본 크기로
            </Button>
            <FormControlLabel
              label="광고 표시"
              control={
                <Checkbox
                  checked={value.showAdMark}
                  onChange={(e) => change('showAdMark', e.target.checked)}
                />
              }
            />
            {['startsAt', 'endsAt'].map((key) => (
              <TextField
                key={key}
                label={key === 'startsAt' ? '공개 시작 (선택)' : '공개 종료 (선택)'}
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={
                  value[key]
                    ? new Date(
                        new Date(value[key]).getTime() -
                          new Date(value[key]).getTimezoneOffset() * 60000,
                      )
                        .toISOString()
                        .slice(0, 16)
                    : ''
                }
                onChange={(e) =>
                  change(key, e.target.value ? new Date(e.target.value).toISOString() : null)
                }
              />
            ))}
            <Box
              aria-label="미니 팝업 미리보기"
              sx={{
                maxWidth: 360,
                position: 'relative',
                minHeight: 52,
                bgcolor: value.backgroundColor,
                color: value.textColor,
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 1,
                gap: 1.25,
              }}
            >
              {value.imageUrl && (
                <Box
                  component="img"
                  src={value.imageUrl}
                  alt=""
                  sx={{ width: 32, height: 32, objectFit: 'cover', borderRadius: '6px' }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    overflowWrap: 'anywhere',
                    color: `${value.textColor} !important`,
                    fontSize: `${value.fontSize ?? 14}px`,
                    lineHeight: 1.5,
                    fontWeight: 500,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {value.title || '미니 팝업 문구'}
                </Typography>
              </Box>
              <span aria-hidden>×</span>
              {value.showAdMark && (
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    right: 4,
                    bottom: 4,
                    fontSize: 6,
                    fontWeight: 500,
                    lineHeight: '8px',
                    color: `${value.textColor} !important`,
                    pointerEvents: 'none',
                  }}
                >
                  광고
                </Box>
              )}
            </Box>
            <FormControlLabel
              label="미니 팝업 공개"
              control={
                <Checkbox
                  checked={value.enabled}
                  onChange={(e) => change('enabled', e.target.checked)}
                />
              }
            />
            {saved && (
              <Alert severity="success">
                저장했습니다. 다음 앱 실행에서 적용됩니다. 오늘 숨긴 기기의 기록은 유지됩니다.
              </Alert>
            )}
            <Button type="submit" variant="contained">
              저장
            </Button>
          </>
        )}
      </Stack>
    </Box>
  );
}
